import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Clock, Instagram, MapPin, MessageCircle, Phone } from "lucide-react";
import { CATEGORIES, CONFIG, OFFERS, waLink } from "./config";
import { trackEvent as track } from "./lib/analytics";
import { ProductsGrid } from "./shop";
import { Chapter, Reveal } from "./motion";
import { Footer, Header, WhatsAppButton } from "./components/layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Page({ children, className = "" }) {
  return (
    <>
      <Header />
      <main className={`inner-page ${className}`}>{children}</main>
      <Footer />
    </>
  );
}

export function Collections() {
  return (
    <Page>
      <Chapter index="—" eyebrow="EXPLORE YOUR STYLE" title={<>Explore our <em>collections</em></>} text="A considered edit for every style, every occasion and every family." />
      <div className="category-grid">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.06}>
            <Link data-testid={`listing-category-${i + 1}`} to={`/collections/${c.slug}`} className="arch-card">
              <div className="arch-frame">
                <img loading="lazy" src={c.image} alt={`${c.name} collection`} />
                <span className="spotlight" />
              </div>
              <div className="arch-meta">
                <span className="arch-index">{String(i + 1).padStart(2, "0")}</span>
                <h3>{c.name}</h3>
                <p>{c.sub}</p>
                <b>Explore <ArrowRight size={14} /></b>
              </div>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.36}>
          <Link data-testid="listing-category-7" to="/collections/wedding" className="arch-card">
            <div className="arch-frame">
              <img loading="lazy" src="/assets/lehenga-display.jpeg" alt="Wedding and bridal lehenga collection at Hyderabad Shopping Mall" />
              <span className="spotlight" />
            </div>
            <div className="arch-meta">
              <span className="arch-index">07</span>
              <h3>Wedding Collection</h3>
              <p>For celebrations big and small</p>
              <b>Explore <ArrowRight size={14} /></b>
            </div>
          </Link>
        </Reveal>
      </div>
    </Page>
  );
}

export function CategoryPage() {
  const { slug } = useParams();
  const category = CATEGORIES.find((c) => c.slug === slug);
  const isWedding = slug === "wedding";
  const name = isWedding ? "Wedding Collection" : category?.name || "Collection";
  return (
    <Page>
      <Chapter
        index="—"
        eyebrow="THE COLLECTION"
        title={<em>{name}</em>}
        text={`Shop ${name.toLowerCase()} online or visit us in-store at Hyderabad Shopping Mall, Nanded.`}
      />
      <ProductsGrid
        params={{ category: slug }}
        emptyText={`Products for ${name} are being added by the store. Message us on WhatsApp for the latest availability.`}
      />
      <Reveal className="category-actions">
        <WhatsAppButton label={`Ask about ${name}`} context={name} testId="category-whatsapp-cta" className="button primary" />
        <Link data-testid="category-collections-link" className="text-link" to="/collections">
          All collections <ArrowRight size={16} />
        </Link>
      </Reveal>
    </Page>
  );
}

export function OffersPage() {
  return (
    <Page>
      <Chapter index="—" eyebrow="CURRENT PROMOTIONS" title={<>Shop more. <em>Save more.</em></>} text="Current offers, available while stocks last." />
      <div className="listing-offers">
        {OFFERS.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.05}>
            <div data-testid={`listing-offer-${i + 1}`} className="listing-offer">
              <span>0{i + 1}</span>
              <h3>{o.title}</h3>
              <strong>{o.detail}</strong>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="availability-note" data-testid="offers-terms-note">
        Offers may be subject to availability and applicable terms.
      </p>
      <Reveal className="center-row">
        <WhatsAppButton label="Ask about offers" context="current offers" testId="offers-whatsapp-cta" className="button primary" />
      </Reveal>
    </Page>
  );
}

export function NewArrivals() {
  return (
    <Page>
      <Chapter index="—" eyebrow="FRESH FOR YOU" title={<>Just <em>arrived</em></>} text="New styles are arriving regularly — shop them online or visit the showroom." />
      <ProductsGrid params={{ new_arrival: true }} emptyText="New arrivals will be listed here as the store adds them. Follow us on Instagram for daily updates." />
    </Page>
  );
}

export function About() {
  return (
    <Page>
      <Chapter index="—" eyebrow="OUR STORY" title={<>Fashion for <em>every occasion</em></>} />
      <Reveal className="prose">
        <p>
          Hyderabad Shopping Mall is a family fashion destination in Nanded, offering a wide selection of women's fashion, ethnic wear, party wear,
          kids wear and more.
        </p>
        <p>
          From everyday kurtis to wedding sarees, our showroom on Hyderabad Road is built for unhurried, joyful family shopping — with fresh
          collections and seasonal offers across the year.
        </p>
        <p>Visit us at Hyderabad Road, Bafna, Near Baba Petrol Pump, Nanded.</p>
      </Reveal>
      <Reveal className="about-photo">
        <img loading="lazy" src="/assets/saree-section.jpeg" alt="Saree section inside Hyderabad Shopping Mall, Nanded" />
      </Reveal>
      <Reveal className="category-actions">
        <a data-testid="about-directions-button" className="button primary" href={CONFIG.mapUrl} target="_blank" rel="noreferrer">
          <MapPin size={16} /> Visit our store
        </a>
        <WhatsAppButton label="Chat with us" testId="about-whatsapp-cta" className="button outline" />
      </Reveal>
    </Page>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", mobile: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      track("enquiry_submitted");
      toast.success("Thank you — your enquiry has been received.");
      setForm({ name: "", mobile: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Chapter index="—" eyebrow="WE'RE HERE TO HELP" title={<>Visit us or <em>send an enquiry</em></>} text="Come explore the collection in person, or message us before your visit." />
      <div className="contact-layout">
        <Reveal className="contact-info">
          <h3>Hyderabad Shopping Mall</h3>
          <div className="address">
            <MapPin size={20} />
            <span data-testid="contact-address">{CONFIG.address}</span>
          </div>
          <a data-testid="contact-call-link" className="contact-row" href={`tel:${CONFIG.phone}`} onClick={() => track("call_click", { source: "contact" })}>
            <Phone size={18} /> {CONFIG.phoneDisplay}
          </a>
          <a data-testid="contact-whatsapp-link" className="contact-row" href={waLink()} target="_blank" rel="noreferrer">
            <MessageCircle size={18} /> WhatsApp: +91 99083 66736
          </a>
          <div data-testid="contact-hours" className="contact-row static">
            <Clock size={18} /> {CONFIG.hoursDisplay}
          </div>
          <a data-testid="contact-instagram-link" className="contact-row" href={CONFIG.instagram} target="_blank" rel="noreferrer">
            <Instagram size={18} /> @hyderabadshoppingmall_
          </a>
          <a data-testid="contact-directions-link" className="text-link" href={CONFIG.mapUrl} target="_blank" rel="noreferrer">
            Get directions <ArrowRight size={16} />
          </a>
          <div className="map-frame small">
            <iframe data-testid="contact-map-embed" title="Hyderabad Shopping Mall map" src={CONFIG.mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form data-testid="enquiry-form" onSubmit={submit} className="enquiry-form">
            <label>
              Name
              <input data-testid="enquiry-name-input" required minLength={2} maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" autoComplete="name" />
            </label>
            <label>
              Mobile number
              <input data-testid="enquiry-mobile-input" type="tel" pattern="[0-9+\-\s]{10,20}" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit mobile number" autoComplete="tel" />
            </label>
            <label>
              Message
              <textarea data-testid="enquiry-message-input" required minLength={3} maxLength={1000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What are you looking for?" />
            </label>
            <button data-testid="enquiry-submit-button" className="button primary" disabled={loading}>
              {loading ? "Sending…" : "Send enquiry"} <ArrowRight size={16} />
            </button>
          </form>
        </Reveal>
      </div>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page>
      <Chapter index="—" eyebrow="YOUR PRIVACY" title="Privacy Policy" />
      <Reveal className="prose">
        <p>We respect your privacy. Information shared through our enquiry form — your name, mobile number and message — is used only to respond to your request.</p>
        <p>We do not sell or share your personal information for unrelated purposes. Enquiry details are stored securely and accessed only by our store team.</p>
        <p>If you would like your enquiry information removed, please contact the store directly.</p>
      </Reveal>
    </Page>
  );
}

export function Terms() {
  return (
    <Page>
      <Chapter index="—" eyebrow="PLEASE NOTE" title="Terms & Conditions" />
      <Reveal className="prose">
        <p>Collections, offers and availability may change without notice. All offers are subject to applicable terms and stock availability at the store.</p>
        <p>Prices, where displayed, are confirmed at the showroom. Images on this website are indicative of the collections available in-store.</p>
        <p>For any questions about an offer or a product, please message us on WhatsApp or visit the store.</p>
      </Reveal>
    </Page>
  );
}
